import { Logger } from '../devtools/logger';
import { clamp } from '../utils/clamp';
import { timing } from '../metrics/timing';
import type { CacheEntry } from './entries/cacheEntry';

const MIN_PERIOD = 15_000; // 15s
const MAX_PERIOD = 300_000; // 5min
const SWEEP_INTERVAL = 30_000; // 30s

const MIN_RETAIN_PERIOD = 15_000; // 15s
const MAX_RETAIN_PERIOD = 1_800_000; // 30min

let __TEST_ONLY_DISABLE_CACHE_GC = false;

export const __testOnlyDisableCacheGC = () => {
  __TEST_ONLY_DISABLE_CACHE_GC = true;
};

export const __testOnlyEnableCacheGC = () => {
  __TEST_ONLY_DISABLE_CACHE_GC = false;
};

export const createGCManager = ({
  nodes,
  removeNode,
  logger,
}: {
  nodes: Map<string, CacheEntry<any, any>>;
  removeNode: (instanceKey: string) => void;
  logger?: Logger;
}) => {
  const candidates = new Set<string>();
  let sweepTimer: ReturnType<typeof setInterval> | null = null;

  const sweep = () => {
    const now = Date.now();

    for (const instanceKey of candidates) {
      const entry = nodes.get(instanceKey);

      if (entry && (entry.lifecycle.validUntil ?? 0) > now) {
        continue;
      }

      candidates.delete(instanceKey);

      if (!entry || entry.lifecycle.attachedSince != null) {
        continue;
      }

      timing.timeCollectNode(() => {
        logger?.debug('collecting node', { instanceKey });

        removeNode(instanceKey);
      });
    }

    if (candidates.size === 0 && sweepTimer !== null) {
      clearInterval(sweepTimer);
      sweepTimer = null;
    }
  };

  const ensureSweeping = () => {
    if (sweepTimer === null) {
      sweepTimer = setInterval(sweep, SWEEP_INTERVAL);
    }
  };

  const extend = (instanceKey: string, validUntil: number) => {
    const entry = nodes.get(instanceKey);

    if (!entry) {
      return;
    }

    if (validUntil > (entry.lifecycle.validUntil ?? 0)) {
      entry.lifecycle.validUntil = validUntil;
    }

    if (
      __TEST_ONLY_DISABLE_CACHE_GC ||
      entry.lifecycle.attachedSince != null ||
      candidates.has(instanceKey)
    ) {
      return;
    }

    entry.lifecycle.queuedAt = Date.now();
    candidates.add(instanceKey);
    ensureSweeping();
  };

  return {
    queue: (instanceKey: string, period: number) => {
      extend(instanceKey, Date.now() + clamp(period, MIN_PERIOD, MAX_PERIOD));
    },
    retain: (instanceKey: string, period = MAX_RETAIN_PERIOD) => {
      extend(
        instanceKey,
        Date.now() + clamp(period, MIN_RETAIN_PERIOD, MAX_RETAIN_PERIOD)
      );
    },
    getAllStatuses: (): Record<
      string,
      { scheduledAt: number; executeAt: number }
    > => {
      const result: Record<string, { scheduledAt: number; executeAt: number }> =
        {};
      for (const instanceKey of candidates) {
        const { lifecycle } = nodes.get(instanceKey) ?? {};

        if (
          lifecycle?.validUntil != null &&
          lifecycle.queuedAt != null &&
          lifecycle.attachedSince == null
        ) {
          result[instanceKey] = {
            scheduledAt: lifecycle.queuedAt,
            executeAt: lifecycle.validUntil,
          };
        }
      }
      return result;
    },
  };
};

export type GCManager = ReturnType<typeof createGCManager>;
